import { TFunction } from 'i18next';

import { numberToWord } from '../../utils';

export function formatIntakeHeader(t: TFunction, firstName: string, coachTopics: string[]): string {
  const hasCoachTopics = coachTopics.length > 0;

  if (!hasCoachTopics) {
    return t('telehealth-core:providerIntersessionSummary.beforeIntakeNoUpdates', {
      firstName: firstName,
    });
  }
  const topicsText = coachTopics.slice(0, 2).join(' and ').toLocaleLowerCase();

  return t('telehealth-core:providerIntersessionSummary.beforeIntakeEngagedWithCoach', {
    firstName: firstName,
    topics: topicsText,
  });
}

export function formatFollowupHeader(
  t: TFunction,
  firstName: string,
  pronouns: string,
  coachTopics: string[],
  numCompletedResources: number,
  reflectionTopics: string[],
): string {
  const hasCoachTopics = coachTopics.length > 0;
  const hasCompletedResources = numCompletedResources && numCompletedResources > 0;
  const hasReflectionTopics = reflectionTopics.length > 0;

  if (!hasCoachTopics && !hasCompletedResources && !hasReflectionTopics) {
    return t('telehealth-core:providerIntersessionSummary.sinceLastSessionXHasntSharedAnyUpdates', {
      firstName: firstName,
    });
  }

  let header = '';
  if (hasCoachTopics || hasCompletedResources) {
    header += t('telehealth-core:providerIntersessionSummary.sinceLastSessionX', {
      firstName: firstName,
    });
  }

  if (hasCoachTopics) {
    const topicsText = coachTopics.slice(0, 2).join(' and ').toLocaleLowerCase();
    header += ` ${t('telehealth-core:providerIntersessionSummary.engagedWithCoachOnTopics', {
      topics: topicsText,
    })}`;
  }

  if (hasCoachTopics && hasCompletedResources) {
    header += ` ${t('telehealth-core:core.and')} `;
  }

  if (hasCompletedResources) {
    header += ` ${t('telehealth-core:providerIntersessionSummary.completedXResources', {
      count: numCompletedResources,
      countWord: numberToWord(numCompletedResources, t),
    })}`;
  }

  if (hasCoachTopics || hasCompletedResources) {
    header += '. ';
  }

  if (hasReflectionTopics) {
    const formattedTopics = reflectionTopics.slice(0, 2).join(' and ').toLocaleLowerCase();
    const pronoun = pronouns?.split('/')[0] ?? 'they';
    header += ` ${t('telehealth-core:providerIntersessionSummary.thisSessionCover', {
      pronoun,
      topics: formattedTopics,
    })}`;
  }

  return header;
}

export function formatBetweenSessionAction(
  t: TFunction,
  hasCoachAccess: boolean,
  coachTopics: string[],
  numCompletedResources: number,
): string {
  const hasCoachTopics = coachTopics.length > 0;
  if (hasCoachAccess) {
    if (!hasCoachTopics && !numCompletedResources) {
      return t('telehealth-core:providerIntersessionSummary.noCoachInteractionsOrResourceActivity');
    }

    if (!hasCoachTopics) {
      return t('telehealth-core:providerIntersessionSummary.noCoachInteractionsCompletedXResources', {
        count: numCompletedResources,
        countWord: numberToWord(numCompletedResources, t),
      });
    }

    const topicsText = coachTopics.slice(0, 2).join(' and ').toLocaleLowerCase();

    if (numCompletedResources) {
      return t('telehealth-core:providerIntersessionSummary.discussedTopicsAndCompletedXResources', {
        topics: topicsText,
        count: numCompletedResources,
        countWord: numberToWord(numCompletedResources, t),
      });
    }
    return t('telehealth-core:providerIntersessionSummary.discussedTopics', { topics: topicsText });
  }

  if (numCompletedResources) {
    return t('telehealth-core:providerIntersessionSummary.completedXResourcesSinceLastSession', {
      count: numCompletedResources,
      countWord: numberToWord(numCompletedResources, t),
    });
  }

  return t('telehealth-core:providerIntersessionSummary.noResourceActivity');
}

export function formatFollowupMeasureTitle(
  t: TFunction,
  currentGadCategory?: string,
  gadDifference?: number,
  currentPhqCategory?: string,
  phqDifference?: number,
): string {
  const hasGad = currentGadCategory !== undefined && gadDifference !== undefined;
  const hasPhq = currentPhqCategory !== undefined && phqDifference !== undefined;

  if (hasGad && hasPhq) {
    const gadImproving = gadDifference < 0;
    const gadWorsening = gadDifference > 0;
    const phqImproving = phqDifference < 0;
    const phqWorsening = phqDifference > 0;

    if (gadWorsening && phqWorsening) {
      return t('telehealth-core:providerIntersessionSummary.scoresDeclining', {
        gad: currentGadCategory,
        phq: currentPhqCategory,
      });
    }

    if (gadImproving && phqImproving) {
      return t('telehealth-core:providerIntersessionSummary.scoresImproving', {
        gad: currentGadCategory,
        phq: currentPhqCategory,
      });
    }

    if (gadDifference === 0 && phqDifference === 0) {
      return t('telehealth-core:providerIntersessionSummary.scoresConsistent', {
        gad: currentGadCategory,
        phq: currentPhqCategory,
      });
    }

    return t('telehealth-core:providerIntersessionSummary.scoresMixed', {
      gad: currentGadCategory,
      phq: currentPhqCategory,
    });
  }

  if (hasGad) {
    if (gadDifference > 0) {
      return t('telehealth-core:providerIntersessionSummary.scoresDecliningGad', { gad: currentGadCategory });
    }
    if (gadDifference < 0) {
      return t('telehealth-core:providerIntersessionSummary.scoresImprovingGad', { gad: currentGadCategory });
    }
    return t('telehealth-core:providerIntersessionSummary.scoresConsistentGad', { gad: currentGadCategory });
  }

  if (hasPhq) {
    if (phqDifference > 0) {
      return t('telehealth-core:providerIntersessionSummary.scoresDecliningPhq', { phq: currentPhqCategory });
    }
    if (phqDifference < 0) {
      return t('telehealth-core:providerIntersessionSummary.scoresImprovingPhq', { phq: currentPhqCategory });
    }
    return t('telehealth-core:providerIntersessionSummary.scoresConsistentPhq', { phq: currentPhqCategory });
  }

  return t('telehealth-core:providerIntersessionSummary.measuresNotCompleted');
}

export function formatIntakeMeasureTitle(
  t: TFunction,
  currentGadCategory?: string,
  currentPhqCategory?: string,
): string {
  if (!currentGadCategory && !currentPhqCategory) {
    return t('telehealth-core:providerIntersessionSummary.measuresNotCompleted');
  }

  return t('telehealth-core:providerIntersessionSummary.baselineMeasuresFromIntake');
}
